import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { type CreatePosUserRequest, type UpdatePosUserRequest } from '@/models/domain/pos-user'
import { posUserService } from '@/services/pos-user-service'
import { useAuthStore } from '@/stores/auth-store'

const POS_USERS_QUERY_KEY = ['pos-users']
const POS_USER_TYPES_QUERY_KEY = ['pos-user-types']

export function usePosUsers() {
  const { selectedSystem } = useAuthStore()

  return useQuery({
    queryKey: [...POS_USERS_QUERY_KEY, selectedSystem],
    queryFn: () => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return posUserService.listAll(selectedSystem)
    },
    enabled: !!selectedSystem,
    staleTime: 5 * 60 * 1000,
  })
}

export function usePosUser(id: number) {
  const { selectedSystem } = useAuthStore()

  return useQuery({
    queryKey: [...POS_USERS_QUERY_KEY, selectedSystem, id],
    queryFn: () => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return posUserService.getById(selectedSystem, id)
    },
    enabled: !!selectedSystem && id > 0,
  })
}

export function usePosUserTypes() {
  return useQuery({
    queryKey: POS_USER_TYPES_QUERY_KEY,
    queryFn: () => posUserService.listTypes(),
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreatePosUser() {
  const queryClient = useQueryClient()
  const { selectedSystem } = useAuthStore()

  return useMutation({
    mutationFn: (request: CreatePosUserRequest) => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return posUserService.create(selectedSystem, request)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: POS_USERS_QUERY_KEY })
    },
  })
}

export function useUpdatePosUser() {
  const queryClient = useQueryClient()
  const { selectedSystem } = useAuthStore()

  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: UpdatePosUserRequest }) => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return posUserService.update(selectedSystem, id, request)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: POS_USERS_QUERY_KEY })
    },
  })
}
